/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { isError } from "@epic-mw/error-types";
import useSWRImmutable from "swr/immutable";

import { useAccountId } from "../account/account.hooks";
import { useApiConfig } from "../api-config";
import { getApiPath } from "../utils";

export interface AodcPolicyRes {
	AODCFailed: boolean;
}

const getAODCPolicy = async (serverUrl?: string): Promise<AodcPolicyRes> => {
	try {
		const response = await fetch(`${getApiPath(serverUrl)}/policy/aodc`);
		const json = await response.json();
		return {
			AODCFailed: json.failed,
		};
	} catch (err) {
		console.error("getAODCPolicy() failed to load", isError(err) && err.message);
		throw err;
	}
};

interface HookRes {
	result: AodcPolicyRes | undefined;
	isLoading: boolean;
	error: any;
}

export const useAccountAODCPolicy = (): HookRes => {
	const accountId = useAccountId();
	const { serverUrl } = useApiConfig();
	const shouldFetch = Boolean(accountId);

	const { data, error } = useSWRImmutable(
		shouldFetch ? [`aodc-${accountId}`, serverUrl] : null,
		(_key, url) => getAODCPolicy(url),
		{ errorRetryCount: 0 },
	);

	return {
		result: data,
		isLoading: Boolean(!data && !error && shouldFetch),
		error,
	};
};
